import { useTranslation } from "react-i18next"
import { ArrowSquareOut, X } from "@phosphor-icons/react"
import { ListRadioItem } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { GenderIcon } from "@/modules/patients/components/gender-icon"
import { useChDuplicatePatientChangedSubscription$data } from "@/modules/patients/manage-duplicates/services/ch/__generated__/useChDuplicatePatientChangedSubscription.graphql"
import { useDeDuplicatePatientChangedSubscription$data } from "@/modules/patients/manage-duplicates/services/de/__generated__/useDeDuplicatePatientChangedSubscription.graphql"

type DuplicatePatient =
  | useChDuplicatePatientChangedSubscription$data["ch_patientChanged"]
  | useDeDuplicatePatientChangedSubscription$data["de_patientChanged"]

const ManageDuplicatesPatientItem = ({
  patient,
  canRemove = true,
  hasError = false,
  onRemove,
  onOpenRecord,
}: {
  patient: DuplicatePatient
  canRemove?: boolean
  hasError?: boolean
  onRemove: () => void
  onOpenRecord: () => void
}) => {
  const { t } = useTranslation()

  return (
    <ListRadioItem
      value={patient.id}
      classNameRadio="order-first ml-0"
      className="group/item hover:bg-surface-hovered data-[state='checked']:bg-primary-surface data-[state='checked']:text-on-primary-surface relative min-w-0 cursor-pointer gap-0"
      data-testid={`duplicate-patient-item-${patient.displayId}`}>
      <div className="flex min-w-0 gap-2">
        <GenderIcon gender={patient.gender} className="h-10 w-10 min-w-10" />
        <div className="min-w-0 text-left">
          <p
            className={cn(
              "truncate pr-10 text-sm",
              hasError ? "text-critical" : "text-on-surface",
              "group-data-[state='checked']/item:text-on-primary-surface",
            )}
            data-testid={`duplicate-patient-name-${patient.displayId}`}>
            {patient.lastName}, {patient.firstName}
          </p>
          <p
            className={cn(
              "text-xs tracking-wide",
              hasError ? "text-critical" : "text-on-surface-subdued",
              "group-data-[state='checked']/item:text-on-primary-surface",
            )}
            data-testid={`duplicate-patient-pid-${patient.displayId}`}>
            {patient.displayId}
            {patient.contactChannel ? ` • ${t(`patient.information.contact.${patient.contactChannel}`)}` : ""}
          </p>
        </div>
      </div>
      <span className="ml-auto hidden group-hover/item:hidden! group-data-[state='checked']/item:inline-flex">
        <Tag type="success" label={t("patient.duplicates.primaryLabel")} />
      </span>
      <div className="absolute right-2 hidden shrink-0 items-center gap-1 group-hover/item:flex">
        <span
          role="button"
          tabIndex={0}
          className="hover:bg-surface-hovered cursor-pointer rounded-full p-1 transition-colors"
          onClick={(e) => {
            e.stopPropagation()
            onOpenRecord()
          }}
          data-testid={`duplicate-patient-open-${patient.displayId}`}>
          <ArrowSquareOut size={20} className="text-on-surface-subdued" />
        </span>
        {canRemove && (
          <span
            role="button"
            tabIndex={0}
            className="hover:bg-surface-hovered cursor-pointer rounded-full p-1 transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
            data-testid={`duplicate-patient-remove-${patient.displayId}`}>
            <X size={20} className="text-on-surface-subdued" />
          </span>
        )}
      </div>
    </ListRadioItem>
  )
}

export { ManageDuplicatesPatientItem, type DuplicatePatient }
